import { useEffect, useRef, useState, useMemo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseSharp";
import ShareIcon from "@mui/icons-material/ShareSharp";

import { useAssetTableDispatch } from "../../../hooks/use-table-context/asset";
import { useFormState } from "../../../hooks/use-form-state";
import { TableCellInputType } from "@components/data-table/types";
import { initialValues } from "@components/data-table/add-new-modal/types";
import { formatAssetForSave } from "../../../utils/format-asset-for-save";
import { useCreateAsset } from "@hooks/use-create/asset";
import { useCreateAssetAssignment } from "@hooks/use-create/asset-assignment";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { useUserContext } from "@hooks/use-auth-context";
import { useCapabilities } from "@hooks/use-capabilities";
import { useUpdateAsset } from "@hooks/use-update/asset";
import { DynamicForm } from "@components/dynamicForm";
import {
  createFormFields,
  createFormTabs,
  createInitialState,
  copyModalLinkToClipboard,
  FormTab,
  getDisplayDamageEntries,
  formatDate,
  toTicketId,
} from "../helperFunctions";
import { DamagesGroupBox } from "./damages-group-box";
import { useFileUpload } from "@hooks/use-file-upload";
import { NotesRepeaterBox } from "./notes-repeater-box";
import { ToolTipIconButton } from "@components/tool-tip-icon-button";
import { AssetTicketsTabTable } from "./asset-tickets-tab-table";
import { AssetAssignmentHistoryTab } from "./asset-assignment-history-tab";
import { YesNoModal } from "@components/yes-no-modal";
import { useCreateTicket } from "@hooks/use-create/ticket";
import { formatTicketForSave } from "@utils/format-ticket-for-save";
import { useTicketConfigContext } from "@hooks/use-config-context/ticket";
import { useCorrespondenceContext } from "@hooks/use-correspondence-context";
import { useCheckUnique, getUniquenessHelperText } from "@hooks/use-check-unique";
import {
  fixedModalDialogContentSx,
  fixedModalPaperSx,
  modalHeaderActionsSx,
  modalTabsContainerSx,
  modalTabsSx,
  modalTitleSx,
} from "@utils/modal-styles";

export const AddAssetModal = ({
  open,
  onClose,
  editData,
}: {
  open?: boolean;
  onClose: () => void;
  editData?: any;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openTabId, setOpenTabId] = useState<number>(0);
  const [liveEditData, setLiveEditData] = useState<any>(editData);
  const [linkAlertOpen, setLinkAlertOpen] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<string>("");
  const [transferNotes, setTransferNotes] = useState("");

  // Derive the currently-saved user ID from liveEditData for comparison
  const savedUserId = useMemo(() => {
    const raw = liveEditData?.meta_box?.asset_to_user_to;
    if (raw == null) return null;
    const first = Array.isArray(raw) ? raw[0] : raw;
    const id = first && typeof first === "object" ? (first as any).id : first;
    return id ? Number(id) : null;
  }, [liveEditData]);
  const [createTicketModalOpen, setCreateTicketModalOpen] = useState<boolean>(false);

  const pendingDamageTicketRef = useRef<string>("");
  const pendingDamageFileIdsRef = useRef<number[]>([]);

  const { createAsset } = useCreateAsset();
  const { createAssignment } = useCreateAssetAssignment();
  const { updateAsset } = useUpdateAsset();
  const { createTicket } = useCreateTicket();
  const { config: ticketConfig } = useTicketConfigContext();
  const { tableColumns, config: assetsConfig, loading: configLoading } = useAssetsConfigContext();
  const { refreshItems } = useAssetTableDispatch();
  const { checkUserAuthentication } = useUserContext();
  const { can } = useCapabilities();
  const canEdit = can("edit_assets");
  const canAssign = can("assign_assets");
  const { files, addFiles, removeFile, clearFiles, uploadFiles } =
    useFileUpload();
  const { createCorrespondenceNote } = useCorrespondenceContext();

  //* Sorting Table Columns into Tabs
  const formTabs: FormTab[] = createFormTabs(
    tableColumns,
    assetsConfig,
    "asset",
    "Asset",
  );

  formTabs.push({
    id: formTabs.length,
    name: "ticket_to_asset_from",
    label: "Tickets",
    fields: [],
  });

  formTabs.push({
    id: formTabs.length,
    name: "assignment_history",
    label: "Assignment History",
    fields: [],
  });

  //* Flat list of all Fields for State Creation
  const formFields: Record<string, any> = useMemo(() => {
    const fields = createFormFields(
      tableColumns,
      assetsConfig,
    );

    fields.title = {
      id: "title",
      name: "title",
      label: "Asset Tag",
      inputType: TableCellInputType.Text,
      required: true,
      options: [],
      taxonomyOptions: [],
      subFields: [],
    };

    return fields;
  }, [tableColumns, assetsConfig]);

  const initialState = createInitialState(
    formFields,
    initialValues,
    liveEditData && liveEditData,
  );

  const getOrUploadDamageFileIds = async (): Promise<number[]> => {
    if (pendingDamageFileIdsRef.current.length > 0) {
      return pendingDamageFileIdsRef.current;
    }

    if (!files || files.length === 0) {
      return [];
    }

    const uploadResults = await uploadFiles(files);
    const ids = uploadResults.map((res) => res.id);

    pendingDamageFileIdsRef.current = ids;
    return ids;
  };

  const { state, setState, handleChange, handleSubmit, handleReset } =
    useFormState({
      initialState,
      onSubmit: async (state) => {
        await checkUserAuthentication();
        // Handle form submission logic here
        setIsLoading(true);

        const payloadState = { ...state };

        //* Force damage_ticket for this submit:
        //  - set by createTicketModalOnYes -> newly created ticket id / ticket number
        //  - set by modal onClose -> both are set to ""
        const pendingTicketNumber = pendingDamageTicketRef.current;
        const pendingTicketId = Number(pendingTicketNumber);

        payloadState.damage_ticket_number = pendingTicketNumber;
        payloadState.damage_ticket_id =
          Number.isInteger(pendingTicketId) && pendingTicketId > 0
            ? pendingTicketId
            : null;
        pendingDamageTicketRef.current = "";

        //* Handles New Notes Repeater entries
        if (payloadState.notes && payloadState.notes !== "") {
          const existingNotes = Array.isArray(payloadState.notes_repeater)
            ? payloadState.notes_repeater
            : [];

          payloadState.notes_repeater = [
            ...existingNotes,
            { notes: payloadState.notes },
          ];

          // Clean up the temporary field used for new note entry
          delete payloadState.notes;
        }

        //* Handles New Damages Group Field entries
        if (
          payloadState.damages_time &&
          payloadState.damages_time !== "" &&
          payloadState.damage_report &&
          payloadState.damage_report !== ""
        ) {
          const existingDamages = Array.isArray(
            payloadState.damages_group_field,
          )
            ? payloadState.damages_group_field
            : [];

          const wpFileIds = await getOrUploadDamageFileIds();

          payloadState.damages_group_field = [
            ...existingDamages,
            {
              damages_time: payloadState.damages_time,
              damage_report: payloadState.damage_report,
              damage_ticket_id: payloadState.damage_ticket_id,
              damage_ticket_number: payloadState.damage_ticket_number,
              damage_file_upload: wpFileIds.map(String),
            },
          ];

          clearFiles();
          pendingDamageFileIdsRef.current = [];

          // Clean up the temporary fields used for new damage entry
          delete payloadState.damages_time;
          delete payloadState.damage_report;
          delete payloadState.damage_ticket_id;
          delete payloadState.damage_ticket_number;
          delete payloadState.damage_file_upload;
        } else {
          // Prevent stale file IDs from leaking into subsequent submissions.
          pendingDamageFileIdsRef.current = [];
        }

        if (editData?.id) {
          // assign_assets only: POST to asset-assignments — backend writes
          // history record AND updates mb_relationships in one call.
          if (canAssign && !canEdit) {
            const userId = Number(payloadState.asset_to_user_to);
            const result = await createAssignment({
              asset_id: Number(editData.id),
              wp_user_id: userId,
              ...(transferNotes.trim() ? { notes: transferNotes.trim() } : {}),
            });
            if (result) {
              const updatedData = {
                ...liveEditData,
                asset_to_user_to: [String(result.wp_user_id)],
                meta_box: {
                  ...liveEditData?.meta_box,
                  asset_to_user_to: [String(result.wp_user_id)],
                },
              };
              setLiveEditData(updatedData);
              setState(createInitialState(formFields, initialValues, updatedData));
              setTransferNotes("");
            }
          } else {
            const formattedPayload = formatAssetForSave(payloadState, assetsConfig);

            if (process.env.NODE_ENV !== "production") {
              console.info("[asset-save][update] damage payload", {
                rawDamages: payloadState.damages_group_field,
                formattedDamages: formattedPayload?.meta_box?.damages_group_field,
              });
            }

            const updatedAsset = await updateAsset(
              editData.id,
              {
                ...formattedPayload,
                ...(transferNotes.trim() ? { transfer_notes: transferNotes.trim() } : {}),
              } as any,
            );
            if (updatedAsset) {
              setLiveEditData(updatedAsset);
              setState(
                createInitialState(formFields, initialValues, updatedAsset),
              );
              setTransferNotes("");
            }
          }
        } else {
          const formattedPayload = formatAssetForSave(payloadState, assetsConfig);

          if (process.env.NODE_ENV !== "production") {
            console.info("[asset-save][create] damage payload", {
              rawDamages: payloadState.damages_group_field,
              formattedDamages: formattedPayload?.meta_box?.damages_group_field,
            });
          }

          await createAsset(formattedPayload);
        }
        setTimeout(async () => {
          await refreshItems();
          setIsLoading(false);
          if (!editData) handleReset();
        }, 500); // Wait for 500ms before refreshing assets

        /* 
      Close modal if we were creating a new asset. 
      If editing, keep it open to show updated info
      */
        !editData && onClose();
      },
    });

  const isConfigReady: boolean = !configLoading && tableColumns.length > 0;
  const hasEditData: boolean = !!editData?.id;

  const currentUserId = state.asset_to_user_to ? Number(state.asset_to_user_to) : null;
  const userHasChanged = !!(editData?.id && currentUserId !== savedUserId);

  // Uniqueness checks for whitelisted fields.
  // Use "" when value is unchanged from the original so the hook skips silently.
  const originalTitle = editData?.title?.rendered ?? "";
  const originalSerial = String(editData?.meta_box?.serial_number ?? "");
  const effectiveTitleValue = String(state.title ?? "") === originalTitle ? "" : String(state.title ?? "");
  const effectiveSerialValue = String(state.serial_number ?? "") === originalSerial ? "" : String(state.serial_number ?? "");

  const assetTagCheck = useCheckUnique({
    type: "asset",
    source: "post_title",
    value: effectiveTitleValue,
    excludeId: editData?.id,
  });
  const serialNumberCheck = useCheckUnique({
    type: "asset",
    source: "post_meta",
    field: "serial_number",
    value: effectiveSerialValue,
    excludeId: editData?.id,
  });
  const fieldHelperTexts = {
    title: getUniquenessHelperText(assetTagCheck.isChecking, assetTagCheck.isUnique, effectiveTitleValue),
    serial_number: getUniquenessHelperText(serialNumberCheck.isChecking, serialNumberCheck.isUnique, effectiveSerialValue),
  };

  //* Keeping liveEditData in sync with prop changes
  useEffect(() => {
    setLiveEditData(editData);
  }, [editData]);

  useEffect(() => {
    if (!hasEditData) return;
    if (!isConfigReady) return;

    const nextState = createInitialState(
      formFields,
      initialValues,
      liveEditData
    )

    setState(nextState);
  }, [hasEditData, isConfigReady, liveEditData, tableColumns, assetsConfig, setState, formFields]);

  const displayDamageEntries = getDisplayDamageEntries(
    liveEditData?.meta_box?.damages_group_field || [],
  );

  const createTicketModalOnYes = async () => {
    const wpFileIds = await getOrUploadDamageFileIds();

    const ticketPayload = {
      ticket_to_asset_to: [String(liveEditData.id)],
      status_ticket: "",
      repair_location: "",
      issue_description: state.damage_report,
      date_of_issue: state.damages_time,
      model_ticket: "",
      damage_type: "",
      files_group: [wpFileIds.map(String)],
    };

    /* 
    * New ticket's Initial Correspondence Note using 
    * info from the Damage/Incident Report.
    */
    const initCorrNote: string =
      `Initial Damage/Incident Report -- ${formatDate(state.damages_time)} -- ${state.damage_report}`;

    setCreateTicketModalOpen(false);

    const newTicket = await createTicket(
      formatTicketForSave(ticketPayload, ticketConfig));

    await createCorrespondenceNote(
      newTicket.id, {
      visibility: "internal",
      note_text: initCorrNote,
    });

    // Ensure submit gets ticket number
    pendingDamageTicketRef.current = newTicket.title.rendered;

    // setReportTicket(newTicket);
    setAlertMsg(`Ticket ${newTicket?.title.rendered} created successfully`);
    setLinkAlertOpen(true);

    setTimeout(() => {
      setLinkAlertOpen(false);
      setAlertMsg("");
    }, 10000); // Alert closes after 10 seconds

    handleSubmit();
  };

  return (
    <Dialog
      keepMounted
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: fixedModalPaperSx }}
    >
      {open ? (
        <>
          {/* Create Ticket Modal */}
          <YesNoModal
            message="Do you want to create a new Ticket from this Damage/Incident Report?"
            isOpen={createTicketModalOpen}
            onClose={() => {
              setCreateTicketModalOpen(false);
              pendingDamageTicketRef.current = "";
              pendingDamageFileIdsRef.current = [];
              handleSubmit();
            }}
            onYes={createTicketModalOnYes}
          />

          <DialogTitle sx={modalTitleSx}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1}
            >
              <Typography variant="h6" mb={1}>
                {editData
                  ? canEdit ? "Edit Asset" : canAssign ? "View and Assign Asset" : "View Asset"
                  : "Add New Asset"}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={modalHeaderActionsSx}
              >
                {editData && can("read_asset") && (
                  <ToolTipIconButton
                    text="Share Asset"
                    icon={<ShareIcon />}
                    onClick={() => copyModalLinkToClipboard(
                      editData.slug,
                      "assets",
                      setLinkAlertOpen,
                      setAlertMsg,
                    )}
                  />
                )}
                <ToolTipIconButton
                  text="Close Asset"
                  icon={<CloseIcon />}
                  onClick={() => onClose()}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} mb={editData ? 1 : 0}>
              {editData && (
                <Box
                  sx={modalTabsContainerSx}
                >
                  <Tabs
                    value={openTabId}
                    textColor="primary"
                    indicatorColor="primary"
                    aria-label="asset modal tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={modalTabsSx}
                  >
                    {formTabs.map((tab: FormTab) => (
                      <Tab
                        key={tab.name}
                        label={tab.label.replace("Damage/", "Damage / ")}
                        value={tab.id}
                        aria-label={tab.label}
                        onClick={() => setOpenTabId(tab.id)}
                      />
                    ))}
                  </Tabs>
                </Box>
              )}
            </Stack>

            {/* Modal Link Alert */}
            {linkAlertOpen && (
              <Alert
                severity="info"
                sx={{ mb: 1 }}
              >
                {alertMsg}
              </Alert>
            )}
          </DialogTitle>

          <DialogContent
            dividers
            sx={fixedModalDialogContentSx}
          >
          {(isLoading || !isConfigReady) && (
            <Box
              sx={{
                flex: 1,
                minHeight: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {!isLoading &&
            isConfigReady &&
            formTabs[openTabId] &&
            formTabs[openTabId].name !== "ticket_to_asset_from" &&
            formTabs[openTabId].name !== "assignment_history" && (
              <DynamicForm
                formFields={formTabs[openTabId].fields}
                state={state}
                handleChange={handleChange}
                handleSubmit={() => {
                  if (formTabs[openTabId].name === "damages_group_field") {
                    setCreateTicketModalOpen(true);
                  } else {
                    handleSubmit();
                  }
                }}
                handleReset={handleReset}
                files={files}
                addFiles={addFiles}
                removeFile={removeFile}
                transferNotes={transferNotes}
                onTransferNotesChange={setTransferNotes}
                showTransferNotes={userHasChanged}
                fieldHelperTexts={fieldHelperTexts}
                readOnly={!!editData && !canEdit}
                assignOnly={!!editData && !canEdit && canAssign}
                afterSubmitContent={
                  editData &&
                  (formTabs[openTabId].name === "damages_group_field" ||
                    formTabs[openTabId].name === "notes_repeater") &&
                  state[formTabs[openTabId].name] &&
                  Array.isArray(state[formTabs[openTabId].name]) &&
                  state[formTabs[openTabId].name].length > 0 ? (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack sx={{ px: 2, pb: 2 }}>
                        {/* Damages Group Field */}
                        {formTabs[openTabId].name === "damages_group_field" && (
                          <Stack>
                            <Box
                              sx={{
                                maxHeight: { xs: 220, sm: 300 },
                                overflowY: "auto",
                                pr: 0.5,
                              }}
                            >
                              {displayDamageEntries.length === 0 ? (
                                <Typography variant="body2" sx={{ py: 1 }}>
                                  No damage/incident reports
                                </Typography>
                              ) : (
                                displayDamageEntries.map((entry: any, idx: number) => (
                                  <DamagesGroupBox
                                    key={idx}
                                    date={entry["damages_time"]}
                                    description={entry["damage_report"]}
                                    fileIds={entry["damage_file_upload"] || []}
                                    ticketId={toTicketId(entry)}
                                    ticketNumber={entry["damage_ticket_number"] || ""}
                                  />
                                ))
                              )}
                            </Box>
                          </Stack>
                        )}

                        {/* Notes Repeater */}
                        {formTabs[openTabId].name === "notes_repeater" && (
                          <Stack>
                            <Box
                              sx={{
                                maxHeight: { xs: 220, sm: 300 },
                                overflowY: "auto",
                                pr: 0.5,
                              }}
                            >
                              {state[formTabs[openTabId].name].map(
                                (entry: any, idx: number) => (
                                  <NotesRepeaterBox
                                    key={idx}
                                    noteBody={entry["notes"]}
                                    date={entry["notes_date"]}
                                    creator={entry["notes_creator"]}
                                  />
                                ),
                              )}
                            </Box>
                          </Stack>
                        )}
                      </Stack>
                    </>
                  ) : null
                }
              />
            )}

          {/* Non-form Tabs */}
          {editData && isConfigReady && !isLoading && (
            formTabs[openTabId].name === "ticket_to_asset_from" ||
            formTabs[openTabId].name === "assignment_history"
          ) && (
              <div className="fixed-modal__tab">
                <div className="fixed-modal__body">
                  {/* Asset Ticket List */}
                  {formTabs[openTabId].name === "ticket_to_asset_from" &&
                    liveEditData.meta_box.ticket_to_asset_from && (
                      <AssetTicketsTabTable entries={liveEditData.meta_box.ticket_to_asset_from} />
                    )
                  }

                  {/* Assignment History */}
                  {formTabs[openTabId].name === "assignment_history" && (
                    <AssetAssignmentHistoryTab assetId={Number(editData.id)} />
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </>
      ) : (
        <></>
      )}
    </Dialog>
  );
};

export default AddAssetModal;
